import React, { useState, lazy, Suspense } from 'react';
import { Badge, Spinner, Button } from 'reactstrap';
import { ChatMessage as ChatMessageType } from '../../services/chatService';
import { User, MessageCircle, AlertCircle, Check, Copy, ThumbsUp, ThumbsDown } from 'react-feather';
import { normalizeTimestamp } from '../../utils/date';
import '../../assets/scss/components/chat/_chat-message.scss';

// Lazy load the specialized card to avoid circular dependencies or heavy bundle on initial load
const GeneratedProductCard = lazy(() => import('../../routes/aichat/components/GeneratedProductCard'));

interface ChatMessageProps {
	message: ChatMessageType;
	isTyping?: boolean;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, isTyping = false }) => {
	const isUser = message.role === 'user';
	const timestamp = normalizeTimestamp(message.timestamp);
	const [copied, setCopied] = useState(false);
	const [feedback, setFeedback] = useState<'positive' | 'negative' | null>(null);

	const formatTime = (date: Date) => {
		return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	};

	const handleCopy = () => {
		navigator.clipboard.writeText(message.content);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	// Simple Markdown-lite parser
	const renderContent = (content: string) => {
		if (!content) return null;
		return content.split('\n').map((line, i) => {
			// Bullet points
			if (line.trim().startsWith('* ') || line.trim().startsWith('- ')) {
				return <li key={i}>{parseInline(line.trim().substring(2))}</li>;
			}
			return <p key={i}>{parseInline(line)}</p>;
		});
	};

	const parseInline = (text: string) => {
		// Bold: **text**
		let parts: (string | JSX.Element)[] = [text];

		// Process Bold
		parts = parts.flatMap(part => {
			if (typeof part !== 'string') return part;
			const subParts = part.split(/(\*\*.*?\*\*)/g);
			return subParts.map(sp => sp.startsWith('**') && sp.endsWith('**')
				? <strong key={sp}>{sp.slice(2, -2)}</strong>
				: sp);
		});

		// Process Italics: *text*
		parts = parts.flatMap(part => {
			if (typeof part !== 'string') return part;
			const subParts = part.split(/(\*.*?\*)/g);
			return subParts.map(sp => sp.length > 2 && sp.startsWith('*') && sp.endsWith('*') && !sp.startsWith('**')
				? <em key={sp}>{sp.slice(1, -1)}</em>
				: sp);
		});

		return parts;
	};

	const getActionTypeBadge = (actionType?: string) => {
		if (!actionType) return null;

		const badgeConfig = {
			new_product: { color: 'primary', text: 'New Product' },
			formulate: { color: 'success', text: 'Formulate' },
			score: { color: 'info', text: 'Score' },
			optimize: { color: 'warning', text: 'Optimize' }
		};

		const config = badgeConfig[actionType as keyof typeof badgeConfig];
		if (!config) return null;

		return (
			<Badge color={config.color} className="action-badge rounded-pill px-2">
				{config.text}
			</Badge>
		);
	};

	const getMessageStatusIcon = () => {
		if (message.role !== 'user' || !message.status) return null;

		switch (message.status) {
			case 'sending':
				return <Spinner size="sm" className="message-status-icon" />;
			case 'sent':
				return <Check size={14} className="message-status-icon sent" />;
			case 'failed':
				return <AlertCircle size={14} className="message-status-icon failed" />;
			default:
				return null;
		}
	};

	return (
		<div className={`chat-message-wrapper ${isUser ? 'user-wrapper' : 'ai-wrapper'}`}>
			<div className={`chat-message ${isUser ? 'user-message' : 'ai-message'} ${message.status === 'failed' ? 'failed' : ''}`}>
				<div className="message-avatar shadow-sm">
					{isUser ? (
						<div className="user-avatar">
							<User size={16} />
						</div>
					) : (
						<div className="ai-avatar">
							<MessageCircle size={16} />
						</div>
					)}
				</div>

				<div className="message-content">
					<div className="message-header">
						<span className="message-sender fw-bold">
							{isUser ? 'You' : 'JourneyAI'}
						</span>
						<span className="message-time">
							{formatTime(timestamp)}
						</span>
						{message.action_type && getActionTypeBadge(message.action_type)}
						{getMessageStatusIcon()}
					</div>

					<div className={`message-bubble shadow-sm ${isTyping ? 'typing' : ''}`}>
						{isTyping ? (
							<div className="typing-indicator">
								<span></span>
								<span></span>
								<span></span>
							</div>
						) : (
							<div className="message-body">
								{renderContent(message.content)}

								{/* Specialized Metadata Rendering (Products, Scores, etc.) */}
								{!isUser && message.metadata && (
									<div className="mt-3">
										<Suspense fallback={<div className="p-3 text-center"><Spinner size="sm" /></div>}>
											{message.metadata.type === 'product_generated' && message.metadata.data && (
												<GeneratedProductCard product={message.metadata.data} />
											)}
											{/* Add other metadata types here (e.g., formulations, scores) */}
										</Suspense>
									</div>
								)}
							</div>
						)}

						{!isTyping && !isUser && (
							<div className="message-actions">
								<Button color="link" size="sm" className="action-btn p-1" onClick={handleCopy}>
									{copied ? <Check size={14} className="text-success" /> : <Copy size={14} />}
								</Button>
								<div className="feedback-group">
									<Button
										color="link" size="sm"
										className={`action-btn p-1 ${feedback === 'positive' ? 'active text-success' : ''}`}
										onClick={() => setFeedback('positive')}
									>
										<ThumbsUp size={14} />
									</Button>
									<Button
										color="link" size="sm"
										className={`action-btn p-1 ${feedback === 'negative' ? 'active text-danger' : ''}`}
										onClick={() => setFeedback('negative')}
									>
										<ThumbsDown size={14} />
									</Button>
								</div>
							</div>
						)}
						{!isTyping && isUser && (
							<Button color="link" size="sm" className="action-btn copy-btn-user p-1" onClick={handleCopy}>
								{copied ? <Check size={14} className="text-white" /> : <Copy size={14} className="text-white-50" />}
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default ChatMessage;